<template>
  <v-container fluid>
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon color="white" class="mr-2">{{ icons.mdiTextBoxSearchOutline }}</v-icon>
        <span>Dev Logs Viewer</span>
      </v-card-title>

      <v-card-text class="pt-6">
        <v-row class="align-center">
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="selectedFile"
              :items="files"
              :search-input.sync="fileSearch"
              item-text="name"
              item-value="identifier"
              label="Select Log File"
              placeholder="Type to search..."
              outlined
              dense
              hide-details
              :loading="loadingFiles"
              :disabled="loadingFiles || !files.length"
              clearable
              @change="onFileChange"
            >
              <template v-slot:prepend-inner>
                <v-icon small>{{ icons.mdiFileDocument }}</v-icon>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">{{ item.size_formatted }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>No log files found</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="perPage"
              :items="perPageOptions"
              label="Per Page"
              outlined
              dense
              hide-details
              @change="onPerPageChange"
            ></v-select>
          </v-col>

          <v-col cols="12" md="5" class="d-flex align-center" style="gap: 12px;">
            <v-text-field
              v-model="searchQuery"
              label="Search logs"
              placeholder="Error text, class, stack content..."
              outlined
              dense
              hide-details
              clearable
              class="flex-grow-1"
              style="max-width: calc(100% - 120px);"
              @keyup.enter="applySearch"
              @click:clear="applySearch"
            >
              <template v-slot:prepend-inner>
                <v-icon small>{{ icons.mdiMagnify }}</v-icon>
              </template>
            </v-text-field>
            <v-btn color="primary" height="40" @click="applySearch" :loading="loadingLogs">
              Search
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="align-center">
          <v-col cols="12" md="5">
            <v-menu
              v-model="menuStartOpen"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatUtcDisplay(startDateUtc, startHourUtc, startMinuteUtc, startSecondUtc)"
                  label="Start (UTC)"
                  hint="Pick date & time in UTC — same numbers as the Timestamp column when logs use UTC."
                  persistent-hint
                  readonly
                  outlined
                  dense
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear.stop="clearStartUtc"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small>{{ icons.mdiCalendarClock }}</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-card flat class="utc-picker-card">
                <v-date-picker
                  v-model="startDateUtc"
                  no-title
                  scrollable
                  @input="onStartDateInput"
                />
                <v-divider />
                <v-card-text class="py-3 px-4 utc-time-fields">
                  <div class="caption mb-2 grey--text text--darken-1">Time (UTC, 24h)</div>
                  <div class="d-flex align-center flex-wrap utc-time-row">
                    <v-text-field
                      v-model.number="startHourUtc"
                      label="Hour"
                      type="number"
                      min="0"
                      max="23"
                      dense
                      outlined
                      hide-details
                      class="utc-num"
                      @blur="normalizeStartTimeParts"
                      @change="onTimestampFilterChange"
                    />
                    <span class="utc-time-sep">:</span>
                    <v-text-field
                      v-model.number="startMinuteUtc"
                      label="Min"
                      type="number"
                      min="0"
                      max="59"
                      dense
                      outlined
                      hide-details
                      class="utc-num"
                      @blur="normalizeStartTimeParts"
                      @change="onTimestampFilterChange"
                    />
                    <span class="utc-time-sep">:</span>
                    <v-text-field
                      v-model.number="startSecondUtc"
                      label="Sec"
                      type="number"
                      min="0"
                      max="59"
                      dense
                      outlined
                      hide-details
                      class="utc-num"
                      @blur="normalizeStartTimeParts"
                      @change="onTimestampFilterChange"
                    />
                  </div>
                </v-card-text>
              </v-card>
            </v-menu>
          </v-col>
          <v-col cols="12" md="5">
            <v-menu
              v-model="menuEndOpen"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatUtcDisplay(endDateUtc, endHourUtc, endMinuteUtc, endSecondUtc)"
                  label="End (UTC)"
                  hint="Inclusive end bound in UTC."
                  persistent-hint
                  readonly
                  outlined
                  dense
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear.stop="clearEndUtc"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small>{{ icons.mdiCalendarClock }}</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-card flat class="utc-picker-card">
                <v-date-picker
                  v-model="endDateUtc"
                  no-title
                  scrollable
                  @input="onEndDateInput"
                />
                <v-divider />
                <v-card-text class="py-3 px-4 utc-time-fields">
                  <div class="caption mb-2 grey--text text--darken-1">Time (UTC, 24h)</div>
                  <div class="d-flex align-center flex-wrap utc-time-row">
                    <v-text-field
                      v-model.number="endHourUtc"
                      label="Hour"
                      type="number"
                      min="0"
                      max="23"
                      dense
                      outlined
                      hide-details
                      class="utc-num"
                      @blur="normalizeEndTimeParts"
                      @change="onTimestampFilterChange"
                    />
                    <span class="utc-time-sep">:</span>
                    <v-text-field
                      v-model.number="endMinuteUtc"
                      label="Min"
                      type="number"
                      min="0"
                      max="59"
                      dense
                      outlined
                      hide-details
                      class="utc-num"
                      @blur="normalizeEndTimeParts"
                      @change="onTimestampFilterChange"
                    />
                    <span class="utc-time-sep">:</span>
                    <v-text-field
                      v-model.number="endSecondUtc"
                      label="Sec"
                      type="number"
                      min="0"
                      max="59"
                      dense
                      outlined
                      hide-details
                      class="utc-num"
                      @blur="normalizeEndTimeParts"
                      @change="onTimestampFilterChange"
                    />
                  </div>
                </v-card-text>
              </v-card>
            </v-menu>
          </v-col>
          <v-col cols="12" md="2" class="caption grey--text text--darken-1">
            Inclusive range. Pick the date on the calendar; type hour, minute, and second in UTC.
          </v-col>
        </v-row>

        <v-row class="mb-2" align="center">
          <v-col cols="12" md="9">
            <div class="d-flex flex-wrap level-chip-wrap">
              <v-chip
                class="ma-1"
                :class="selectedLevel === null ? 'chip-selected' : 'chip-unselected'"
                :color="selectedLevel === null ? 'primary' : 'default'"
                small
                @click="setLevelFilter(null)"
              >
                All ({{ totalFromLevelCounts }})
              </v-chip>

              <v-chip
                v-for="level in levelCounts"
                :key="level.level"
                class="ma-1"
                small
                :class="(selectedLevel === level.level || selectedLevel === level.level.toLowerCase()) ? 'chip-selected' : 'chip-unselected'"
                :color="(selectedLevel === level.level || selectedLevel === level.level.toLowerCase()) ? 'primary' : 'default'"
                @click="setLevelFilter(level.level)"
              >
                {{ level.level_name }} ({{ level.count }})
              </v-chip>
            </div>
          </v-col>

          <v-col cols="12" md="3" class="d-flex justify-end">
            <v-btn color="secondary" @click="refreshCurrent" :loading="loadingLogs">
              <v-icon left>{{ icons.mdiRefresh }}</v-icon>
              Refresh
            </v-btn>
          </v-col>
        </v-row>

        <v-alert v-if="statusMessage" dense outlined type="info" class="mb-4">
          {{ statusMessage }}
        </v-alert>

        <v-data-table
          :headers="headers"
          :items="logs"
          item-key="_key"
          class="elevation-1"
          :loading="loadingLogs"
          disable-pagination
          hide-default-footer
          show-expand
          :single-expand="true"
          :expanded.sync="expanded"
          :key="utcFilterKey"
        >
          <template v-slot:[`item.level_name`]="{ item }">
            <v-chip x-small :class="`log-chip-${(item.level || '').toLowerCase()}`">
              {{ item.level_name }}
            </v-chip>
          </template>

          <template v-slot:[`item.message`]="{ item }">
            <div style="max-width: 100%; word-wrap: break-word; white-space: normal; overflow: hidden; text-overflow: ellipsis;">
              {{ item.message || '-' }}
            </div>
          </template>

          <template v-slot:[`item.datetime`]="{ item }">
            <span class="text-no-wrap">{{ item.datetime || '-' }}</span>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="expanded-log-cell">
              <div class="mb-2"><strong>Message</strong></div>
              <pre class="log-pre log-pre-expanded">{{ item.message || '-' }}</pre>

              <div class="mb-2 mt-4"><strong>Full Text</strong></div>
              <pre class="log-pre log-pre-expanded">{{ item.complete_text || item.full_text || '-' }}</pre>
            </td>
          </template>

          <template v-slot:no-data>
            <div class="py-6 text-center">
              <img :src="require('@/assets/images/misc/no-data.png')" alt="No logs" width="220" />
              <p class="mb-0 mt-2">No log entries found for this filter.</p>
            </div>
          </template>
        </v-data-table>

        <div class="d-flex justify-space-between align-center mt-4">
          <div class="caption grey--text text--darken-1">
            Showing {{ pagination.from || 0 }} to {{ pagination.to || 0 }} of {{ pagination.total || 0 }} entries
          </div>

          <v-pagination
            v-model="page"
            :length="pagination.last_page || 1"
            :total-visible="7"
            @input="fetchLogs"
          ></v-pagination>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import {
  mdiTextBoxSearchOutline,
  mdiMagnify,
  mdiRefresh,
  mdiFileDocument,
  mdiCalendarClock,
} from '@mdi/js'

export default {
  data() {
    return {
      icons: {
        mdiTextBoxSearchOutline,
        mdiMagnify,
        mdiRefresh,
        mdiFileDocument,
        mdiCalendarClock,
      },
      menuStartOpen: false,
      menuEndOpen: false,
      startDateUtc: null,
      startHourUtc: null,
      startMinuteUtc: null,
      startSecondUtc: null,
      endDateUtc: null,
      endHourUtc: null,
      endMinuteUtc: null,
      endSecondUtc: null,
      headers: [
        { text: 'Timestamp', value: 'datetime', width: 180 },
        { text: 'Level', value: 'level_name', width: 130 },
        { text: 'Message', value: 'message' },
        { text: '', value: 'data-table-expand', width: 60 },
      ],
      files: [],
      selectedFile: null,
      fileSearch: '',
      logs: [],
      levelCounts: [],
      selectedLevel: null,
      searchQuery: '',
      meta: {
        log_datetime_format: 'Y-m-d H:i:s',
        log_timezone: '',
        filter_range_timezone: 'UTC',
      },
      page: 1,
      perPage: 50,
      perPageOptions: [25, 50, 100, 250],
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: 0,
        from: 0,
        to: 0,
      },
      loadingFiles: false,
      loadingLogs: false,
      expanded: [],
      statusMessage: '',
    }
  },

  computed: {
    totalFromLevelCounts() {
      return (this.levelCounts || []).reduce((sum, level) => sum + (level.count || 0), 0)
    },
    utcFilterKey() {
      const p = [
        this.selectedLevel,
        this.selectedFile,
        this.page,
        this.startDateUtc,
        this.startHourUtc,
        this.startMinuteUtc,
        this.startSecondUtc,
        this.endDateUtc,
        this.endHourUtc,
        this.endMinuteUtc,
        this.endSecondUtc,
      ]
      return p.join('|')
    },
  },

  async mounted() {
    await this.fetchFiles()
  },

  methods: {
    async fetchFiles() {
      this.loadingFiles = true
      try {
        const response = await axios.get('admin/dev-logs/files')
        const payload = response?.data || {}
        const files = payload.data || []
        this.files = files
        if (payload.meta) {
          this.meta = { ...this.meta, ...payload.meta }
        }

        if (!files.length) {
          this.statusMessage = 'No log files were found.'
          return
        }

        // Prefer laravel.log if it exists, else fallback to first available file.
        const laravelLog = files.find(file => file.name === 'laravel.log')
        this.selectedFile = laravelLog ? laravelLog.identifier : files[0].identifier
        await this.fetchLogs()
      } catch (error) {
        this.$swal.fire('Error', error?.response?.data?.message || 'Failed to load log files.', 'error')
      } finally {
        this.loadingFiles = false
      }
    },

    async fetchLogs() {
      if (!this.selectedFile) {
        return
      }

      this.loadingLogs = true
      this.expanded = []

      try {
        const response = await axios.get('admin/dev-logs/entries', {
          params: {
            file: this.selectedFile,
            page: this.page,
            per_page: this.perPage,
            query: this.searchQuery || undefined,
            level: this.selectedLevel || undefined,
            direction: 'desc',
            start_timestamp: this.toUtcIsoFromParts(
              this.startDateUtc,
              this.startHourUtc,
              this.startMinuteUtc,
              this.startSecondUtc
            ),
            end_timestamp: this.toUtcIsoFromParts(
              this.endDateUtc,
              this.endHourUtc,
              this.endMinuteUtc,
              this.endSecondUtc
            ),
          },
        })

        const payload = response?.data || {}
        if (payload.meta) {
          this.meta = { ...this.meta, ...payload.meta }
        }

        // Stable unique keys per row: Date.now() was identical for all items in one map(),
        // and duplicate log.index from the API caused duplicate keys (VDataTable item-key).
        const newLogs = (payload.data || []).map((log, idx) => ({
          ...log,
          _key: `${this.selectedFile}-${this.page}-${idx}`,
        }))

        this.logs = [] // Clear first to force re-render
        this.$nextTick(() => {
          this.logs = newLogs
        })

        this.levelCounts = payload.level_counts || []
        this.pagination = Object.assign({}, this.pagination, payload.pagination || {})
        this.page = this.pagination.current_page || 1

        const hasMoreScan = payload?.pagination?.has_more_results
        const scanPercent = payload?.pagination?.percent_scanned
        this.statusMessage = hasMoreScan
          ? `Partial scan loaded (${scanPercent || 0}% scanned). Refine search or refresh for more indexed data.`
          : ''
      } catch (error) {
        console.error('Fetch error:', error?.response?.data || error?.message || error)
        this.$swal.fire('Error', error?.response?.data?.message || 'Failed to load log entries.', 'error')
      } finally {
        this.loadingLogs = false
      }
    },

    onFileChange() {
      this.page = 1
      this.selectedLevel = null
      this.startDateUtc = null
      this.startHourUtc = null
      this.startMinuteUtc = null
      this.startSecondUtc = null
      this.endDateUtc = null
      this.endHourUtc = null
      this.endMinuteUtc = null
      this.endSecondUtc = null
      this.menuStartOpen = false
      this.menuEndOpen = false
      this.fetchLogs()
    },

    /** Display-only: UTC wall clock as YYYY-MM-DD HH:mm:ss (matches typical log-viewer Timestamp). */
    formatUtcDisplay(dateStr, hour, minute, second) {
      if (!dateStr) {
        return ''
      }
      const hh = String(this.clampHour(hour)).padStart(2, '0')
      const mm = String(this.clampMinSec(minute)).padStart(2, '0')
      const ss = String(this.clampMinSec(second)).padStart(2, '0')
      return `${dateStr} ${hh}:${mm}:${ss}`
    },

    clampHour(v) {
      if (v === null || v === undefined || v === '') {
        return 0
      }
      const n = parseInt(v, 10)
      if (Number.isNaN(n)) {
        return 0
      }
      return Math.min(23, Math.max(0, n))
    },

    clampMinSec(v) {
      if (v === null || v === undefined || v === '') {
        return 0
      }
      const n = parseInt(v, 10)
      if (Number.isNaN(n)) {
        return 0
      }
      return Math.min(59, Math.max(0, n))
    },

    normalizeStartTimeParts() {
      this.startHourUtc = this.clampHour(this.startHourUtc)
      this.startMinuteUtc = this.clampMinSec(this.startMinuteUtc)
      this.startSecondUtc = this.clampMinSec(this.startSecondUtc)
    },

    normalizeEndTimeParts() {
      this.endHourUtc = this.clampHour(this.endHourUtc)
      this.endMinuteUtc = this.clampMinSec(this.endMinuteUtc)
      this.endSecondUtc = this.clampMinSec(this.endSecondUtc)
    },

    onStartDateInput() {
      if (this.startDateUtc) {
        if (this.startHourUtc === null || this.startHourUtc === '') {
          this.startHourUtc = 0
        }
        if (this.startMinuteUtc === null || this.startMinuteUtc === '') {
          this.startMinuteUtc = 0
        }
        if (this.startSecondUtc === null || this.startSecondUtc === '') {
          this.startSecondUtc = 0
        }
        this.normalizeStartTimeParts()
      }
      this.onTimestampFilterChange()
    },

    onEndDateInput() {
      if (this.endDateUtc) {
        if (this.endHourUtc === null || this.endHourUtc === '') {
          this.endHourUtc = 0
        }
        if (this.endMinuteUtc === null || this.endMinuteUtc === '') {
          this.endMinuteUtc = 0
        }
        if (this.endSecondUtc === null || this.endSecondUtc === '') {
          this.endSecondUtc = 0
        }
        this.normalizeEndTimeParts()
      }
      this.onTimestampFilterChange()
    },

    clearStartUtc() {
      this.startDateUtc = null
      this.startHourUtc = null
      this.startMinuteUtc = null
      this.startSecondUtc = null
      this.menuStartOpen = false
      this.page = 1
      this.fetchLogs()
    },

    clearEndUtc() {
      this.endDateUtc = null
      this.endHourUtc = null
      this.endMinuteUtc = null
      this.endSecondUtc = null
      this.menuEndOpen = false
      this.page = 1
      this.fetchLogs()
    },

    /** Build ISO-8601 UTC instant from UTC date + numeric H/M/S (not local zone). */
    toUtcIsoFromParts(dateStr, hour, minute, second) {
      if (!dateStr) {
        return undefined
      }
      const hh = String(this.clampHour(hour)).padStart(2, '0')
      const mm = String(this.clampMinSec(minute)).padStart(2, '0')
      const ss = String(this.clampMinSec(second)).padStart(2, '0')
      const m = moment.utc(`${dateStr} ${hh}:${mm}:${ss}`, 'YYYY-MM-DD HH:mm:ss')
      if (!m.isValid()) {
        return undefined
      }
      return m.toISOString()
    },

    onTimestampFilterChange() {
      if (this.startDateUtc) {
        this.normalizeStartTimeParts()
      }
      if (this.endDateUtc) {
        this.normalizeEndTimeParts()
      }
      this.page = 1
      this.fetchLogs()
    },

    onPerPageChange() {
      this.page = 1
      this.fetchLogs()
    },

    applySearch() {
      this.page = 1
      this.fetchLogs()
    },

    setLevelFilter(level) {
      // Convert level to lowercase to match backend expectations
      this.selectedLevel = level ? level.toLowerCase() : null
      this.page = 1
      this.fetchLogs()
    },

    refreshCurrent() {
      this.fetchLogs()
    },
  },
}
</script>

<style lang="scss" scoped>
.expanded-log-cell {
  background: #fafafa;
  padding: 20px !important;
}

.log-pre {
  margin: 0;
  padding: 12px;
  background: #1f2937;
  color: #e5e7eb;
  border-radius: 6px;
  max-height: none;
  min-height: 150px;
  overflow: auto;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  word-wrap: break-word;
}

.log-pre-expanded {
  max-height: none !important;
  min-height: 300px !important;
  height: auto !important;
  max-width: 100% !important;
}

.log-chip-error {
  background: #ffebee;
  color: #b71c1c;
}

.log-chip-warning {
  background: #fff8e1;
  color: #e65100;
}

.log-chip-info,
.log-chip-notice {
  background: #e3f2fd;
  color: #0d47a1;
}

.log-chip-debug {
  background: #ede7f6;
  color: #4527a0;
}

.chip-selected {
  color: white !important;
}

.chip-unselected {
  color: #333 !important;
  background-color: #e0e0e0 !important;
}

.utc-picker-card {
  max-height: min(90vh, 560px);
  overflow-y: auto;
}

.utc-time-fields {
  max-width: 340px;
}

.utc-time-row {
  gap: 6px;
}

.utc-num {
  flex: 0 0 88px;
  max-width: 96px;
}

.utc-time-sep {
  font-size: 1.25rem;
  font-weight: 500;
  padding: 0 2px;
  margin-top: -8px;
}
</style>
